import { getType } from '@/utils/tool'
import { getEchartsColorPalette, getCssColorVar } from '@/utils/chart-theme-colors'
import { formatTimeStamp } from '@/utils/time'
import {
  applyEchartsFontToOption,
  echartsTooltipFontFamilyCss,
  withEchartsTextStyle,
} from '@/utils/echarts-option/font'

export interface AssignConfig {
  /** 单位 */
  unit?: string,
  /** log 坐标系 */
  log?: string
}

/** 调色盘颜色列表（运行时读取当前主题主色） */
export function getColorDefault (): string[] {
  return getEchartsColorPalette()
}


/** grid（containLabel：把坐标轴刻度文字算进绘图区，避免左侧 Y 轴标签被裁切） */
export const GRID_DEFAULT_OPTION = {
  top: 8,
  bottom: 60,
  left: 40,
  right: 20,
  containLabel: true,
}

/** legend（函数形式：颜色运行时读取 CSS 变量，主题切换重绘后生效） */
export function getLegendDefaultOption () {
  return {
    show: true,
    type: 'scroll',
    left: 'center',
    bottom: 0,
    itemWidth: 12,
    textStyle: withEchartsTextStyle({
      color: getCssColorVar('--cm-color-text-secondary', '#888888'),
    }),
    icon: 'path://M0 1a1 1 0 0 1 1 -1h10a1 1 0 0 1 1 1v0a1 1 0 0 1 -1 1h-10a1 1 0 0 1 -1 -1z'
  }
}

/** xAxis（函数形式：颜色运行时读取 CSS 变量，主题切换重绘后生效） */
export function getXAxisDefaultOption () {
  return {
    type: 'category',
    axisTick: {
      alignWithLabel: true
    },
    axisLine: {
      show: true,
      lineStyle: {
        color: getCssColorVar('--cm-color-border-lighter', 'rgba(221, 221, 221, 0.5)')
      }
    },
    axisLabel: {
      textStyle: withEchartsTextStyle({
        color: getCssColorVar('--cm-color-text-secondary', '#888'),
        lineHeight: 15,
      }),
      padding: [0, 5, 0, 5],
      margin: 10,
    },
  }
}

export function getYAxisDefaultOption (config = {} as AssignConfig) {
  const unitString = config.unit ? ` ${config.unit}` : ''
  return {
    splitLine: {
      lineStyle: {
        type: 'dotted'
      },
    },
    axisLine: {
      show: true,
      lineStyle: {
        color: getCssColorVar('--cm-color-border-lighter', 'rgba(221, 221, 221, 0.5)'),
      },
    },
    axisLabel: {
      ...withEchartsTextStyle({ color: getCssColorVar('--cm-color-text-secondary', '#888') }),
      formatter: (e) => {
        const value = config.log ? e - 1 : e
        return `${value} ${unitString}`
      }
    }
  }
}

function getTooltipHeadHtml (param) {
  const fontCss = echartsTooltipFontFamilyCss()
  return `
<div style="
  background: linear-gradient(94.45deg, rgba(229, 69, 69, 0.02) 17.87%, rgba(229, 69, 69, 0) 118.43%);
  margin: -10px -10px 2px;
  padding: 10px 20px 0;
  color: ${getCssColorVar('--cm-color-text-primary', '#222')};
  font-size: 14px;
  ${fontCss}
">${param.name}
</div>
`
}

function getTooltipRowHtml (param, config = {} as AssignConfig) {
  const unitHtml = config.unit ? `<span> ${config.unit}</span>` : ''
  let markerColor
  if (param?.color?.type === 'linear') {
    markerColor = gradientEchartsToCss(param.color)
  } else {
    markerColor = param.color
  }
  const fontCss = echartsTooltipFontFamilyCss()
  return `
<div class="tooltip-item" style="display: flex; justify-content: space-between; width: 100%;
  padding: 0px 10px; box-sizing: border-box; color: ${getCssColorVar('--el-text-color-primary', '#222')}; font-size: 14px; ${fontCss}"
>
  <div style="display: flex; align-items: center">
    <span class="marker" style="display: inline-block; margin-right:4px; border-radius: 6px; width: 6px; height:6px; background: ${markerColor};"></span
    ><span style="margin-right: 20px">${param.seriesName}：</span>
  </div>
  <span><span style="color: ${getCssColorVar('--cm-color-text-primary', '#222')}; font-size: 14px">${param.value}</span>${unitHtml}</span>
</div>
`
}

export function gradientEchartsToCss ({ x, y, x2, y2, colorStops }) {
  const deg = Math.atan2(y2 - y, x2 - x) / 2 / Math.PI * 360 - 90
  const colorString = colorStops
    .sort((a, b) => b.offset - a.offset)
    .map(v => `${v.color} ${(1 - v.offset) * 100}%`)
    .join(', ')
  return `linear-gradient(${deg}deg, ${colorString})`
}

export function getTooltipFormatter (params, config = {} as AssignConfig) {
  if (Object.prototype.toString.call(params) === '[object Object]') {
    params = [params]
  }
  let htmlString = getTooltipHeadHtml(params[0])
  params.forEach((param) => {
    htmlString += getTooltipRowHtml(param, config)
  })
  return htmlString
}
export const getToolTipDefaultStyle = (config = {}) => ({
  trigger: 'axis',
  borderWidth: 1,
  confine: true,
  extraCssText: `border-radius: 20px;box-shadow: 3px 4px 12px rgba(0, 0, 0, 0.12);line-height: 1.8;-webkit-backdrop-filter: blur(20px);backdrop-filter: blur(20px);`,
  appendToBody: true,
  /* 深色半透明是 ECharts 默认底，项目 formatter 输出深色文字，须显式浅/深底随主题切换 */
  backgroundColor: getCssColorVar('--cm-color-tool-bg', '#ffffff'),
  backdropFilter: 'blur(10px)',
  borderColor: getCssColorVar('--cm-color-border-base', '#ebeef5'),
  ...config
})
export function getTooltipDefaultOption (config = {} as AssignConfig) {
  return {
    ...getToolTipDefaultStyle(),
    formatter: (params) => {
      params.forEach(e => {
        let value = e.value
        // 数组格式 [timestamp, value]
        if (Array.isArray(e.value)) {
          value = e.value[1]
          e.name = formatTimeStamp(e.value[0])
        }
        value = Number(value || 0)
        e.value = config.log ? value - 1 : value
        e.value = +e.value.toFixed(2)
      })
      return getTooltipFormatter(params, config)
    }
  }
}

export function getZoomInsideDefaultOption () {
  return {
    type: 'inside',
    start: 0,
    end: 40,
    zoomOnMouseWheel: false,
    moveOnMouseMove: false,
    moveOnMouseWheel: true,
    rangeMode: 'percent',
    filterMode: 'empty',
    throttle: 0,
  }
}

export function getZoomOutsideDefaultOption (color = 'rgba(159, 159, 159, 0.4)') {
  return {
    start: 0,
    end: 40,
    zoomLock: true,
    height: 8,
    brushSelect: false,
    showDetail: false,
    backgroundColor: 'transparent',
    borderColor: color,
    handleStyle: {
      borderColor: color,
    },
    fillerColor: color,
    selectedDataBackground: {
      lineStyle: {
        color: 'transparent',
      },
      areaStyle: {
        color: 'transparent',
      }
    },
    dataBackground: {
      lineStyle: {
        opacity: 0
      },
      areaStyle: {
        opacity: 0
      }
    }
  }
}

/**
 * 合并数据分支逻辑 递归
 * @param original 原始值
 * @param current 当前并入的值
 * 原始值为object，并入值为object => 双方属性合并
 * 原始值为object，并入值为array => 并入值每项均与原始值合并
 * 其他 => 直接使用并入值
 */
function assignRef (original, current) {
  if (getType(current) === 'object' && getType(original) === 'object') {
    return deepAssign(original, current)
  } else if (getType(current) === 'array' && getType(original) === 'object') {
    return current.map(v => assignRef(original, v))
  }
  return current
}

/** 深度合并，两层递归 */
export function deepAssign (...params) {
  const result = {}
  params.forEach((item) => {
    Object.entries(item).forEach(([key, value]) => {
      result[key] = result[key] || {}
      // assignRef 函数会递归调用deepAssign
      result[key] = assignRef(result[key], value)
    })
  })
  return result
}

export function formatLogOption (option, config: AssignConfig = {}) {
  if (!config.log) {
    return option
  }

  let max = 0
  option[config.log] = option[config.log] || {}
  option[config.log].type = 'log'
  option.series.forEach(seriesItem => {
    seriesItem.data = seriesItem.data.map(v => {
      const value = Number(v || 0) + 1
      max = Math.max(value, max)
      return value
    })
  })
  // 为什么是4.5，4代表分成4个刻度（有的分成5个，未明），0.5是为了代表图形不贴边，最高为在最后一行中点
  const logBase = +Math.pow(max, 1 / 4.5).toFixed()
  option[config.log].logBase = logBase === 1 ? 2 : logBase
  return option
}
